import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Plus } from '@factorialco/f0-react/icons/app'
import {
  IncentivesCompensationPlanDetailMetricsDocument,
  IncentivesCompensationPlanDetailMetricFragment,
} from 'generated/resources/graphql'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { getPayoutRuleSummary } from 'modules/incentives/lib/payoutRules'
import i18n from 'modules/core/lib/i18n'

interface CompensationPlanMetricsProps {
  periodId: string
}

export function CompensationPlanMetrics({ periodId }: CompensationPlanMetricsProps) {
  const source = useGraphqlDataCollectionSource(
    {
      document: IncentivesCompensationPlanDetailMetricsDocument,
      perPage: 20,
      mapData: (data) => data.incentives.compensationPlanMetricsConnection.nodes || [],
      mapPagination: (data) => ({
        total: data.incentives.compensationPlanMetricsConnection.totalCount ?? 0,
      }),
      mapVariables: () => ({
        periodIds: [periodId],
      }),
      secondaryActions: {
        expanded: 1,
        actions: () => [
          {
            label: i18n.t('incentives.detail.metrics.add_metric'),
            icon: Plus,
            onClick: () => {},
          },
        ],
      },
    },
    [periodId]
  )

  return (
    <OneDataCollection
      fullHeight
      source={source}
      visualizations={[
        {
          type: 'table',
          options: {
            columns: [
              {
                label: i18n.t('incentives.detail.metrics.list.name'),
                render: (metric: IncentivesCompensationPlanDetailMetricFragment) => {
                  const compensableMetric = metric.compensableMetric
                  if (compensableMetric?.__typename === 'GoalsMetricGroup') {
                    return {
                      type: 'text' as const,
                      value: compensableMetric.title,
                    }
                  }

                  return undefined
                },
              },
              {
                label: i18n.t('incentives.detail.metrics.list.progress'),
                render: (metric: IncentivesCompensationPlanDetailMetricFragment) => {
                  const compensableMetric = metric.compensableMetric
                  if (
                    compensableMetric?.__typename === 'GoalsMetricGroup' &&
                    typeof compensableMetric.summary?.averageProgress === 'number'
                  ) {
                    return {
                      type: 'progressBar' as const,
                      value: {
                        value: compensableMetric?.summary?.averageProgress,
                        label: `${i18n.formatNumber(compensableMetric?.summary?.averageProgress)}%`,
                      },
                    }
                  }

                  return {
                    type: 'text' as const,
                    value: '-',
                  }
                },
              },
              {
                label: i18n.t('incentives.detail.metrics.list.weight'),
                render: (metric: IncentivesCompensationPlanDetailMetricFragment) => ({
                  type: 'dotTag' as const,
                  value: {
                    label: `${i18n.formatNumber(metric.weight)}%`,
                    color: 'grey' as const,
                  },
                }),
              },
              {
                label: i18n.t('incentives.detail.metrics.list.threshold'),
                align: 'right' as const,
                render: (metric: IncentivesCompensationPlanDetailMetricFragment) => {
                  const { threshold } = getPayoutRuleSummary(metric.payoutRules)

                  return {
                    type: 'text' as const,
                    value: threshold !== null ? `${i18n.formatNumber(threshold)}%` : '-',
                  }
                },
              },
              {
                label: i18n.t('incentives.detail.metrics.list.cap'),
                align: 'right' as const,
                render: (metric: IncentivesCompensationPlanDetailMetricFragment) => {
                  const { cap } = getPayoutRuleSummary(metric.payoutRules)

                  return {
                    type: 'text' as const,
                    value: cap !== null ? `${i18n.formatNumber(cap)}%` : '-',
                  }
                },
              },
            ],
          },
        },
      ]}
    />
  )
}
